import React, { useState, useEffect, useRef, useContext } from 'react';
import './Calendar.css';
import { format, addMonths, endOfMonth, startOfMonth, getDay, isSameMonth, isSameDay, lastDayOfMonth, subMonths, getDate, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import logComponent from '../../common/logComponent';
import { CardScheduleContext } from '../../contexts/CardScheduleContext';

const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function Calendar({ events, onDateClick, visibleMonth, selectedDate, eventsLoading, eventsError, reload, setVisibleMonth, moduleLoadingStatus }) {
  const cardScheduleContext = useContext(CardScheduleContext);
  const [now, setNow] = useState(new Date());
  const [todaysDate, setTodaysDate] = useState(now.getDate());
  const [displayMonth, setDisplayMonth] = useState(format(visibleMonth, 'MMMM yyyy'));
  const [isCurrentMonth, setIsCurrentMonth] = useState(isSameMonth(now, visibleMonth));
  const [endingBlankSpaces, setEndingBlankSpaces] = useState([]);
  const [startingBlankSpaces, setStartingBlankSpaces] = useState([]);
  const [dateNums, setDateNums] =  useState([]);
  const dateClicked = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('Calendar')) { console.debug('Calendar onMount'); }
    let end = getDate(endOfMonth(visibleMonth));
    let start = getDate(startOfMonth(visibleMonth));

    let startDay = getDay(startOfMonth(visibleMonth));
    let endDay = getDay(endOfMonth(visibleMonth));

    let lastDayOfPrevMonth = getDate(lastDayOfMonth(subMonths(visibleMonth, 1)));


    let endingBlankSpacesLocal = [];
    let startingBlankSpacesLocal = [];
    let dateNumsLocal = [];

    for (let i = 0; i < 6 - endDay; i++) {
      endingBlankSpacesLocal.push(i + 1);
    }

    for (let i = 0; i < startDay; i++) {
      startingBlankSpacesLocal.push(lastDayOfPrevMonth - startDay + i + 1);
    }

    for (let i = start; i <= end; i++) {
      dateNumsLocal.push(i);
    }
    setEndingBlankSpaces(endingBlankSpacesLocal);
    setStartingBlankSpaces(startingBlankSpacesLocal);
    setDateNums(dateNumsLocal);

    setIsCurrentMonth(isSameMonth(now, visibleMonth));
    setDisplayMonth(format(visibleMonth, 'MMMM yyyy'));
  }, [visibleMonth]);


  const nextMonth = () => {
    if (logComponent('Calendar')) { console.debug('Calendar nextMonth'); }
    let newDate = addMonths(visibleMonth, 1);
    setVisibleMonth(newDate);
  }

  const prevMonth = () => {
    if (logComponent('Calendar')) {console.debug('Calendar prevMonth'); }
    let newDate = subMonths(visibleMonth, 1);
    setVisibleMonth(newDate);
  }

  const hasEvent = (day) => {
    if (logComponent('Calendar')) {console.debug('Calendar hasEvent'); }

    if (!events || !Array.isArray(events)) return false;

    let theDate = new Date(visibleMonth);

    theDate.setDate(day);
      if (events) {
        return events.some(event => isSameDay(parseISO(event.localStartOn), theDate));
      }
      return null;
  }

  const handleDateClick = (e) => {
    if (logComponent('Calendar')) {console.debug('Calendar handleDateClick'); }
    dateClicked.current = true;
    const clickedDate = new Date(visibleMonth);
    clickedDate.setDate(e.currentTarget.dataset.date);
    selectedDate = clickedDate;

    if (onDateClick) onDateClick(clickedDate);
    const { setScheduleSelectedDate } = cardScheduleContext;
    setScheduleSelectedDate(clickedDate);
  }

  const getDateNumClass = (dateNum) => {
    if (logComponent('Calendar')) {console.debug('Calendar getDateNumClass'); }


    // if the date is selected
    if (selectedDate && dateNum === selectedDate.getDate()) {
      return ' selected-day';
    } else if (hasEvent(dateNum)) {
      return ' has-event';
    } else if (isCurrentMonth && todaysDate === dateNum)  {
      return ' is-today';
    }

    return '';
  }




  return (
    <div className="Calendar">
      <div className="Calendar__month-row">
        <button 
          className="Calendar__month-control" 
          aria-label="Previous Month"
          onClick={prevMonth}>
          <FontAwesomeIcon icon="chevron-left"></FontAwesomeIcon>
        </button>  
        <span className="Calendar__month-name">{ displayMonth }</span>
        <button 
          className="Calendar__month-control" 
          aria-label="Next Month"
          onClick={nextMonth}>
          <FontAwesomeIcon icon="chevron-right"></FontAwesomeIcon>
        </button>
      </div>
      <div className="Calendar__body">
        <div className="Calendar__heading">
          { dayNames.map(name => <span className='Calendar__day-name' key={name}>{ name }</span>) }
        </div>
      { 
        <div className="Calendar__dates">
        {
          events &&
          <div className="Calendar__date-numbers">
          { startingBlankSpaces.map((blank, index) => <span className="Calendar__light-date-num" key={`pre-${index}`}>{ blank }</span>) }
          { dateNums.map(dateNum => {
              const linkDate = new Date(visibleMonth);
              linkDate.setDate(dateNum);
              return (
                <button 
                  onClick={handleDateClick}
                  className={'Calendar__date-num' + getDateNumClass(dateNum)}
                  key={dateNum}
                  data-date={dateNum}>
                  <span className="Calendar__date-num-text">{ dateNum }</span>
                </button>
              );
                
            })
          }
          { endingBlankSpaces.map((blank, index) => <span className="Calendar__light-date-num" key={`post-${index}`}>{ blank }</span>) }
          </div>
        }            
            <div className="Calendar__loading-status">
              {moduleLoadingStatus
                ? moduleLoadingStatus
                : <ModuleLoadingStatus isLoading={eventsLoading} isError={eventsError} reloadAction={reload} />}
          </div>
        </div>  
      }         
      </div>
    </div>
  );
}